import React, { FormEvent, ChangeEvent, KeyboardEvent, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ReactCountryFlag from "react-country-flag";
import { useAppContext } from "../../../../context/AppContext";
import { DISABLE_RTL_STYLES, DISABLE_RTL_PROPS } from "../../../../utils/rtlUtils";
import { COMMON_MUI_STYLES } from "../../../../styles";
import { PasswordField } from "./PasswordField";
import { EmailSpellCheckHint } from "./EmailSpellCheckHint";
import { ICountryData } from "../../../../services/api";
import { checkmark } from "../../../../assets";
import { getFlagCodeFromCountryName } from "../utils/countryUtils";

interface SignupData {
  first_name: string;
  last_name: string;
  email: string;
  country_id: string;
  phone_number: string;
  password: string;
  confirmPassword: string;
}

interface SignupFormProps {
  signupData: SignupData;
  signupErrors: { [key: string]: string };
  formState: {
    signupStep: "email" | "password";
    emailVerified: boolean;
    showPassword: boolean;
    showConfirmPassword: boolean;
  };
  countries: ICountryData[];
  countriesLoading: boolean;
  flagsLoading: { [key: string]: boolean };
  privacyConsent: boolean;
  isEmailVerificationLoading: boolean;
  isRetryingOtp: boolean;
  isSignupLoading: boolean;
  onSignupChange: (e: ChangeEvent<HTMLInputElement> | any) => void;
  onNameKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onSignupSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onEmailVerificationSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onTogglePassword: () => void;
  onToggleConfirmPassword: () => void;
  onPrivacyConsentChange: (value: boolean) => void;
  onSetCurrentView: (view: any) => void;
  onSetFlagsLoading: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  onApplySignupEmailSuggestion: (email: string) => void;
}

export const SignupForm: React.FC<SignupFormProps> = ({
  signupData,
  signupErrors,
  formState,
  countries,
  countriesLoading,
  flagsLoading,
  privacyConsent,
  isEmailVerificationLoading,
  isRetryingOtp,
  isSignupLoading,
  onSignupChange,
  onNameKeyDown,
  onSignupSubmit,
  onEmailVerificationSubmit,
  onTogglePassword,
  onToggleConfirmPassword,
  onPrivacyConsentChange,
  onSetCurrentView,
  onSetFlagsLoading,
  onApplySignupEmailSuggestion,
}) => {
  const { getTranslation } = useAppContext();
  const [signupEmailBlurTick, setSignupEmailBlurTick] = useState(0);

  return (
    <Box
      sx={{
        width: "100%",
        textAlign: "center",
        mt: 2,
        pb: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Typography sx={{ ...COMMON_MUI_STYLES.title1, mb: 3 }}>
        {getTranslation("Txt6")}
      </Typography>

      <Box
        component="form"
        onSubmit={onSignupSubmit}
        noValidate
        sx={{
          display: "flex",
          flexDirection: "column",
          width: { xs: "90%", sm: "35%" },
          justifyContent: "center",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          <TextField
            margin="normal"
            fullWidth
            id="first_name"
            placeholder={getTranslation("Txt708")}
            name="first_name"
            autoComplete="given-name"
            value={signupData.first_name}
            onChange={onSignupChange}
            onKeyDown={onNameKeyDown}
            error={!!signupErrors.first_name}
            helperText={signupErrors.first_name}
            disabled={
              formState.signupStep === "password" || formState.emailVerified
            }
            InputProps={{
              style: (formState.signupStep === "password" || formState.emailVerified) 
                ? { 
                    color: "var(--color-text-secondary)",
                    WebkitTextFillColor: "var(--color-text-secondary)",
                  } 
                : undefined,
            }}
            sx={{ 
              mb: 1,
              ...COMMON_MUI_STYLES.textField,
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "var(--color-text-secondary) !important",
                color: "var(--color-text-secondary) !important",
                cursor: "not-allowed",
              },
              "& .MuiOutlinedInput-root.Mui-disabled": {
                "& fieldset": {
                  borderColor: "var(--color-border-light) !important",
                },
              },
            }}
          />
          <TextField
            margin="normal"
            fullWidth
            id="last_name"
            placeholder={getTranslation("Txt710")}
            name="last_name"
            autoComplete="family-name"
            value={signupData.last_name}
            onChange={onSignupChange}
            onKeyDown={onNameKeyDown}
            error={!!signupErrors.last_name}
            helperText={signupErrors.last_name}
            disabled={
              formState.signupStep === "password" || formState.emailVerified
            }
            InputProps={{
              style: (formState.signupStep === "password" || formState.emailVerified) 
                ? { 
                    color: "var(--color-text-secondary)",
                    WebkitTextFillColor: "var(--color-text-secondary)",
                  } 
                : undefined,
            }}
            sx={{
              mb: 1,
              ...COMMON_MUI_STYLES.textField,
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "var(--color-text-secondary) !important",
                color: "var(--color-text-secondary) !important",
                cursor: "not-allowed",
              },
              "& .MuiOutlinedInput-root.Mui-disabled": {
                "& fieldset": {
                  borderColor: "var(--color-border-light) !important",
                },
              },
            }}
          />
          <Box 
            {...DISABLE_RTL_PROPS}
            sx={{ 
              width: "100%",
              ...DISABLE_RTL_STYLES,
            }}
          >
            <TextField
              {...DISABLE_RTL_PROPS}
              margin="normal"
              fullWidth
              id="phone_number"
              placeholder={getTranslation("Txt953")}
              name="phone_number"
              autoComplete="tel-national"
              value={signupData.phone_number}
              onChange={onSignupChange}
              error={!!signupErrors.phone_number || !!signupErrors.country_id}
              helperText={
                signupErrors.phone_number || signupErrors.country_id || ""
              }
              disabled={
                formState.signupStep === "password" || formState.emailVerified
              }
              inputProps={{
                inputMode: "tel",
                pattern: "[0-9]*",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    {...DISABLE_RTL_PROPS}
                    sx={{
                      p: 0,
                      m: 0,
                      height: "100%",
                      maxHeight: "none",
                      marginLeft: "0 !important",
                      alignSelf: "stretch",
                      "& .MuiInputAdornment-positionStart": {
                        marginRight: 0,
                        marginLeft: "0 !important",
                      },
                      ...DISABLE_RTL_STYLES,
                    }}
                  >
                    <Box
                      {...DISABLE_RTL_PROPS}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: "#ffffff",
                        height: "100%",
                        minHeight: "56px",
                        padding: "10px !important",
                        ...DISABLE_RTL_STYLES,
                      }}
                    >
                      <Select
                        {...DISABLE_RTL_PROPS}
                        variant="standard"
                        disableUnderline
                        id="country_id"
                        name="country_id"
                        value={signupData.country_id}
                        onChange={onSignupChange}
                        displayEmpty
                        autoComplete="off"
                        disabled={
                          formState.signupStep === "password" ||
                          formState.emailVerified ||
                          (countries.length === 1 && !!countries[0]?._id)
                        }
                        renderValue={(selected) => {
                          const selectedCountry = countries.find(
                            (c) => c._id === selected
                          );

                          // Fallback while country API / country_id is not ready
                          const dialCode = selectedCountry?.dial_code ?? "91";
                          const flagCode = selectedCountry
                            ? getFlagCodeFromCountryName(
                                selectedCountry.country_name
                              ) || selectedCountry.country_code
                            : "IN";

                          return (
                            <Box
                              component="span"
                              sx={{
                                display: "flex",
                                alignItems: "flex-end",
                                gap: 0.5,
                                height: "100%",
                                lineHeight: 1,
                              }}
                              {...DISABLE_RTL_PROPS}
                            >
                              {flagCode && (
                                <Box
                                  sx={{
                                    position: "relative",
                                    width: "1.2em",
                                    height: "1.2em",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    lineHeight: 1,
                                  }}
                                >
                                  {flagsLoading[flagCode] !== false && (
                                    <Skeleton
                                      variant="circular"
                                      width="1.2em"
                                      height="1.2em"
                                      sx={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                      }}
                                    />
                                  )}
                                  <ReactCountryFlag
                                    countryCode={flagCode}
                                    svg
                                    style={{
                                      width: "1.2em",
                                      height: "1.2em",
                                      display: "block",
                                      opacity:
                                        flagsLoading[flagCode] === false
                                          ? 1
                                          : 0,
                                      transition: "opacity 0.2s",
                                    }}
                                    title={flagCode}
                                    loading="lazy"
                                    onLoad={() => {
                                      onSetFlagsLoading((prev) => ({
                                        ...prev,
                                        [flagCode]: false,
                                      }));
                                    }}
                                  />
                                </Box>
                              )}
                              <Box
                                component="span"
                                sx={{
                                  display: "block",
                                  fontWeight: 500,
                                  lineHeight: "1em",
                                }}
                              >
                                +{dialCode}
                              </Box>
                            </Box>
                          );
                        }}
                        sx={{
                          minWidth: { xs: "70px", sm: "85px" },
                          maxWidth: { xs: "90px", sm: "110px" },
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          ...DISABLE_RTL_STYLES,
                          "& .MuiSelect-select": {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            minHeight: "0 !important",
                            lineHeight: 1,
                            paddingTop: 0,
                            paddingBottom: 0,
                            fontWeight: 500,
                            cursor: "pointer",
                            caretColor: "transparent",
                            textAlign: "left !important",
                            direction: "ltr !important",
                            "&:focus": {
                              caretColor: "transparent",
                            },
                          },
                          "& input": {
                            caretColor: "transparent",
                            textAlign: "left !important",
                            direction: "ltr !important",
                          },
                          "& svg": {
                            color: "var(--color-text-primary)",
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: { maxHeight: 300 },
                            sx: {
                              ...DISABLE_RTL_STYLES,
                              "& .MuiList-root": {
                                paddingLeft: 1,
                                paddingRight: 1,
                                ...DISABLE_RTL_STYLES,
                              },
                              "& .MuiMenuItem-root": {
                                paddingLeft: 1,
                                ...DISABLE_RTL_STYLES,
                              },
                            },
                          },
                        }}
                      >
                        {countriesLoading ? (
                          <MenuItem key="loading-countries" disabled>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                          </MenuItem>
                        ) : countries.length > 0 ? (
                          countries.map((country, index) => {
                            const countryCode = country.country_code;
                            const flagCode = getFlagCodeFromCountryName(country.country_name) || countryCode;
                            return (
                              <MenuItem
                                key={country._id || `country-${index}`}
                                value={country._id || ""}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    width: "100%",
                                    justifyContent: "flex-start",
                                    gap: 1,
                                  }}
                                >
                                  {flagCode && (
                                    <Box sx={{ position: 'relative', width: "1.2em", height: "1.2em", display: 'flex', alignItems: 'center' }}>
                                      {flagsLoading[flagCode] !== false && (
                                        <Skeleton
                                          variant="circular"
                                          width="1.2em"
                                          height="1.2em"
                                          sx={{ position: 'absolute', top: 0, left: 0 }}
                                        />
                                      )}
                                      <ReactCountryFlag
                                        countryCode={flagCode}
                                        svg
                                        style={{
                                          width: "1.2em",
                                          height: "1.2em",
                                          opacity: flagsLoading[flagCode] === false ? 1 : 0,
                                          transition: 'opacity 0.2s',
                                        }}
                                        title={flagCode}
                                        loading="lazy"
                                        onLoad={() => {
                                          onSetFlagsLoading(prev => ({ ...prev, [flagCode]: false }));
                                        }}
                                      />
                                    </Box>
                                  )}
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {country.country_name || ""}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary" }}
                                  >
                                    +{country.dial_code}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            );
                          })
                        ) : (
                          <MenuItem key="no-countries" disabled>
                            No countries available
                          </MenuItem>
                        )}
                      </Select>
                    </Box>
                  </InputAdornment>
                ),
              }}
              sx={{
                ...DISABLE_RTL_STYLES,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0.3125rem",
                  paddingLeft: "0 !important",
                  display: "flex",
                  alignItems: "stretch", 
                  backgroundColor: "#fff",
                  "& .MuiInputAdornment-root": {
                    height: "100%",
                    alignSelf: "stretch",
                  },
                  "&:hover": {
                    backgroundColor: "#fff",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#fff",
                  },
                  "& fieldset": {
                    border: "1.5px solid var(--color-border)",
                  },
                  "&:hover fieldset": {
                    border: "1.5px solid var(--color-border-dark)",
                  },
                  "&.Mui-focused fieldset": {
                    border: "1.5px solid #1976d2",
                  },
                },
                "& .MuiInputBase-input": {
                  borderRadius: "0 !important",
                  backgroundColor: "#fff !important",
                  textAlign: "left !important",
                  direction: "ltr !important",
                  "&:focus": {
                    backgroundColor: "#fff !important",
                  },
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "var(--color-text-secondary) !important",
                  color: "var(--color-text-secondary) !important",
                  cursor: "not-allowed",
                  textAlign: "left !important",
                  direction: "ltr !important",
                },
                "& .MuiOutlinedInput-root.Mui-disabled": {
                  "& fieldset": {
                    borderColor: "var(--color-border-light) !important",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--color-border-light) !important",
                  },
                },
              }}
              style={{ direction: "ltr" }}
            />
          </Box>
          <TextField
            margin="normal"
            fullWidth
            id="signupEmail"
            placeholder={getTranslation("Txt712")}
            name="email"
            autoComplete="email"
            value={signupData.email}
            onChange={onSignupChange}
            onBlur={() => {
              if (!formState.emailVerified && !isEmailVerificationLoading) {
                setSignupEmailBlurTick((t) => t + 1);
              }
            }}
            error={!!signupErrors.email}
            helperText={
              <Box component="div" sx={{ width: "100%" }}>
                {signupErrors.email ? (
                  <Typography variant="caption" component="div" color="error" sx={{ display: "block", mb: 0.25 }}>
                    {signupErrors.email}
                  </Typography>
                ) : null}
                <EmailSpellCheckHint
                  email={signupData.email}
                  disabled={formState.emailVerified || isEmailVerificationLoading}
                  blurRecheckTick={signupEmailBlurTick}
                  onApplySuggestion={onApplySignupEmailSuggestion}
                />
              </Box>
            }
            FormHelperTextProps={{ component: "div" }}
            disabled={
              formState.signupStep === "password" || formState.emailVerified
            }
            InputProps={{
              endAdornment: formState.emailVerified ? (
                <InputAdornment position="end" sx={{ m: 0 }}>
                  <Box
                    component="img"
                    src={checkmark}
                    alt="Email verified"
                    sx={{
                      width: "20px",
                      height: "20px",
                      objectFit: "contain",
                    }}
                  />
                </InputAdornment>
              ) : undefined,
              style: formState.emailVerified ? { color: "var(--color-text-secondary)" } : undefined,
            }}
            sx={{ mb: 1, ...COMMON_MUI_STYLES.textField }}
          />
          <Button
            variant="text"
            disabled={formState.emailVerified || isEmailVerificationLoading}
            onClick={() => {
              const mockEvent = {
                preventDefault: () => {},
              } as React.FormEvent<HTMLFormElement>;
              onEmailVerificationSubmit(mockEvent);
            }}
            sx={{
              textTransform: "none",
              fontSize: "0.875rem",
              color: formState.emailVerified ? "var(--color-text-secondary)" : "var(--color-text-primary)",
              textDecoration: "underline",
              padding: "4px 8px",
              minWidth: "auto",
              alignSelf: "flex-end",
              marginLeft: "auto",
              "&:hover": {
                backgroundColor: formState.emailVerified ? "transparent" : "rgba(25, 118, 210, 0.04)",
                textDecoration: "underline",
              },
              "&.Mui-disabled": {
                color: "var(--color-text-secondary)",
                textDecoration: "underline",
              },
            }}
          >
            {isEmailVerificationLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: "var(--color-text-primary)" }} />
              </Box>
            ) : formState.emailVerified ? getTranslation("Txt839") : getTranslation("Txt837", "Verify Email")}
          </Button>

          <>
            <PasswordField
              name="password"
              autoComplete="new-password"
              placeholder={getTranslation("Txt714")}
              value={signupData.password}
              onChange={onSignupChange}
              showPassword={formState.showPassword}
              onTogglePassword={onTogglePassword}
              error={!!signupErrors.password}
              helperText={signupErrors.password}
            />
            <PasswordField
              name="confirmPassword"
              autoComplete="new-password"
              placeholder={getTranslation("Txt716")}
              value={signupData.confirmPassword}
              onChange={onSignupChange}
              showPassword={formState.showConfirmPassword}
              onTogglePassword={onToggleConfirmPassword}
              error={!!signupErrors.confirmPassword}
              helperText={signupErrors.confirmPassword}
            />
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mt: 2, gap: 1 }}>
              <Box
                onClick={() => onPrivacyConsentChange(!privacyConsent)}
                sx={{
                  width: "20px",
                  height: "20px",
                  minWidth: "20px",
                  minHeight: "20px",
                  cursor: "pointer",
                  border: "2px solid var(--color-border-dark)",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: privacyConsent ? "var(--color-primary)" : "transparent",
                  transition: "all 0.2s ease",
                  flexShrink: 0,
                  "&:hover": {
                    borderColor: "var(--color-border-hover)",
                    backgroundColor: privacyConsent ? "#666" : "#f0f0f0",
                  },
                }}
              >
                {privacyConsent && (
                  <CheckIcon
                    sx={{
                      width: "14px",
                      height: "14px",
                      color: "var(--color-text-inverse)",
                    }}
                  />
                )}
              </Box>
              <Typography variant="body2" sx={{ fontSize: "0.75rem", color: "var(--color-text-primary)", fontWeight: 400, textAlign: "left" }}>
                {getTranslation("Txt843")}{" "}
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    fontSize: "0.75rem",
                    color: "var(--color-text-primary)",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": {
                      color: "var(--color-text-primary)",
                    },
                  }}
                  onClick={() => {
                    const currentStep = `signup-${formState.signupStep}`;
                    sessionStorage.setItem('privacyPolicyPrevView', currentStep);
                    
                    const formDataToStore = {
                      first_name: signupData.first_name,
                      last_name: signupData.last_name,
                      email: signupData.email,
                      country_id: signupData.country_id,
                      phone_number: signupData.phone_number,
                      password: signupData.password,
                      confirmPassword: signupData.confirmPassword
                    };
                    sessionStorage.setItem('signupFormData', JSON.stringify(formDataToStore));
                    sessionStorage.setItem('signupStep', formState.signupStep);
                    sessionStorage.setItem('emailVerified', formState.emailVerified.toString());
                    
                    onSetCurrentView("privacy-policy");
                  }}
                >
                  {getTranslation("Txt844")}
                </Typography>
                .
              </Typography>
            </Box>
          </>
        </Box>

        <Button
          type="submit"
          variant="contained"
          disabled={
            formState.signupStep === "email" 
              ? (isEmailVerificationLoading || isRetryingOtp || !privacyConsent) 
              : (isSignupLoading || !privacyConsent)
          }
          sx={{
            ...COMMON_MUI_STYLES.primaryCtaButton1,
            mt: 3,
            mb: 2,
            position: 'relative',
            '& > *:not(.loader-overlay)': {
              opacity: isSignupLoading ? 0 : 1,
              transition: 'opacity 0.2s ease-in-out',
            },
          }}
        >
          <span>{getTranslation("Txt94")}</span>
          {isSignupLoading && (
            <CircularProgress 
              size={20} 
              sx={{ 
                color: "var(--color-text-inverse)", 
                position: 'absolute', 
                top: '50%', 
                left: '50%', 
                marginTop: '-10px',
                marginLeft: '-10px',
              }} 
              className="loader-overlay"
            />
          )}
        </Button>
      </Box>

      <Typography
        variant="body1"
        sx={{
          color: "var(--color-text-primary)",
          fontWeight: 700,
          textAlign: "center",
          mt: 2,
          mb: "calc(env(safe-area-inset-bottom, 0px) + 4px)",
        }}
      >
        {getTranslation("Txt896").includes("?") ? getTranslation("Txt896") : getTranslation("Txt896")+"?"}{" "}
        <Button
          variant="text"
          size="medium"
          onClick={() => onSetCurrentView("contact")}
          sx={{
            textTransform: "none",
            color: "var(--color-text-primary)",
            fontWeight: 700,
            textDecoration: "underline",
            p: 0,
            minWidth: 0,
            fontSize: "1.1rem",
            "&:hover": {
              backgroundColor: "transparent",
              textDecoration: "underline",
            },
          }}
        >
          {getTranslation("Txt10")}
        </Button>
      </Typography>
    </Box>
  );
};
